import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { Fingerprint, Plus, Trash2, RefreshCw, Pencil, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const pad = base64.length % 4 === 0 ? "" : "=".repeat(4 - (base64.length % 4));
  const binary = atob(base64 + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export default function PasskeysSection({ token }) {
  const [passkeys, setPasskeys] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const isSupported = typeof window !== "undefined" &&
    window.PublicKeyCredential !== undefined &&
    typeof window.PublicKeyCredential === "function";

  const fetchPasskeys = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/passkeys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPasskeys(res.data);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { fetchPasskeys(); }, [fetchPasskeys]);

  const registerPasskey = async () => {
    setIsRegistering(true);
    try {
      // 1. Get registration options
      const optRes = await axios.post(`${API}/admin/passkeys/register-options`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const options = JSON.parse(optRes.data.options);

      // Convert base64url to ArrayBuffer
      options.challenge = base64urlToBuffer(options.challenge);
      options.user.id = base64urlToBuffer(options.user.id);
      if (options.excludeCredentials) {
        options.excludeCredentials = options.excludeCredentials.map(c => ({
          ...c, id: base64urlToBuffer(c.id)
        }));
      }

      // 2. Call WebAuthn API
      const credential = await navigator.credentials.create({ publicKey: options });

      // 3. Send response to server
      const credentialJSON = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          attestationObject: bufferToBase64url(credential.response.attestationObject),
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
        },
      };
      if (credential.response.getTransports) {
        credentialJSON.response.transports = credential.response.getTransports();
      }

      await axios.post(`${API}/admin/passkeys/register-verify`, {
        credential: credentialJSON,
        name: newName.trim() || undefined,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Clé d'accès enregistrée");
      setNewName("");
      fetchPasskeys();
    } catch (err) {
      if (err.name === "NotAllowedError") {
        toast.error("Enregistrement annulé");
      } else {
        toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const deletePasskey = async (credentialId) => {
    try {
      await axios.delete(`${API}/admin/passkeys/${encodeURIComponent(credentialId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Clé d'accès supprimée");
      fetchPasskeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const renamePasskey = async (credentialId) => {
    if (!editName.trim()) return;
    try {
      await axios.patch(`${API}/admin/passkeys/${encodeURIComponent(credentialId)}`, {
        name: editName.trim(),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingId(null);
      setEditName("");
      fetchPasskeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const formatDate = (d) => {
    if (!d) return "Jamais";
    return new Date(d).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <div className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-xl p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${passkeys.length > 0 ? "bg-cyan-500/20" : "bg-zinc-800"}`}>
          <Fingerprint className={`w-5 h-5 ${passkeys.length > 0 ? "text-cyan-400" : "text-zinc-500"}`} />
        </div>
        <div className="flex-1">
          <h2 className="text-white font-medium flex items-center gap-2">
            Clés d'accès (Passkeys)
            {passkeys.length > 0 && (
              <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 text-[10px]">
                {passkeys.length} clé{passkeys.length > 1 ? "s" : ""}
              </Badge>
            )}
          </h2>
          <p className="text-xs text-zinc-500">
            Connectez-vous avec Face ID, Touch ID ou Windows Hello
          </p>
        </div>
      </div>

      {!isSupported ? (
        <div className="p-3 border border-zinc-700 bg-zinc-800/50 rounded-lg">
          <p className="text-zinc-400 text-sm">
            Les clés d'accès ne sont pas supportées par ce navigateur.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Existing passkeys */}
          {passkeys.map((pk) => (
            <div key={pk.credential_id} className="flex items-center gap-3 p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg">
              <Fingerprint className="w-4 h-4 text-cyan-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                {editingId === pk.credential_id ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="bg-zinc-900 border-zinc-600 text-white text-sm h-7 py-0"
                      autoFocus
                      onKeyDown={(e) => e.key === "Enter" && renamePasskey(pk.credential_id)}
                    />
                    <button onClick={() => renamePasskey(pk.credential_id)} className="text-green-400 hover:text-green-300">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => setEditingId(null)} className="text-zinc-500 hover:text-white transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-white font-medium truncate">{pk.name}</p>
                    <p className="text-[10px] text-zinc-500">
                      Créée le {formatDate(pk.created_at)}
                      {pk.last_used && ` · Dernière utilisation: ${formatDate(pk.last_used)}`}
                    </p>
                  </>
                )}
              </div>
              {editingId !== pk.credential_id && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setEditingId(pk.credential_id); setEditName(pk.name); }}
                    className="text-zinc-500 hover:text-amber-400 p-1 transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => deletePasskey(pk.credential_id)} className="text-red-400 hover:text-red-300 p-1">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* Register new */}
          <div className="space-y-2 pt-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nom de la clé (optionnel)"
              className="bg-zinc-900 border-zinc-700 text-white text-sm"
            />
            <Button
              onClick={registerPasskey}
              disabled={isRegistering}
              className="w-full bg-cyan-600 hover:bg-cyan-700 text-white text-sm"
            >
              {isRegistering ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Ajouter une clé d'accès
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
